import {DecimalPipe, PercentPipe} from '@angular/common'
import {
  Component,
  computed,
  effect,
  inject,
  Injectable,
  OnInit
} from '@angular/core'
import {
  AbstractControl,
  AsyncValidator,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms'
import {
  MatError,
  MatFormField,
  MatHint,
  MatLabel,
  MatSuffix
} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {FormatNumberDirective} from '@sparbanken-syd/sparbanken-syd-theme'
import {filter, Observable, of} from 'rxjs'
import {debounceTime, switchMap, take} from 'rxjs/operators'
import {HemnetService} from '../../services/hemnet.service'
import {InterestService} from '../../services/interest.service'


@Injectable({providedIn: 'root'})
export class DownPaymentValidator implements AsyncValidator {
  private readonly service = inject(HemnetService)

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    if (control.value > this.service.askingPrice()) {
      return of({
        tooLarge: true
      })
    }
    return of(control.value < this.service.minimumDownPayment()
      ? {minDownPayment: true}
      : null)
  }
}

@Component({
  selector: 'spb-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [ReactiveFormsModule, MatHint, MatError, FormatNumberDirective, MatFormField, MatInput, MatLabel, MatSuffix, MatIcon, PercentPipe, FormsModule, DecimalPipe]
})
export class InputComponent implements OnInit {
  public DELAY = 3000

  public readonly downPaymentValidator = inject(DownPaymentValidator)

  public readonly hemnetService = inject(HemnetService)

  public inputForm: FormGroup = new FormGroup({
    askingPrice: new FormControl<number>(0, [
      Validators.required, Validators.min(1), Validators.max(99999999)]),
    downPayment: new FormControl<number>(0, {
      validators: [Validators.required],
      asyncValidators: this.downPaymentValidator.validate.bind(this.downPaymentValidator)
    }),
    interestRate: new FormControl<number>(0, [Validators.required, Validators.min(0.01)]),
    fee: new FormControl<number>(0, [Validators.max(99999)]),
    runCost: new FormControl<number>(0, [Validators.max(999999)])
  })

  public readonly interestService = inject(InterestService)

  public readonly isListRate = computed(() =>
    this.hemnetService.interestRate() === this.interestService.listRate())

  get downPayment() {
    return this.inputForm.controls.downPayment
  }

  constructor() {
    effect(() => {
      // This should only happen once.
      this.inputForm.controls.interestRate.setValue(this.interestService.interestRate())
    })
    effect(() => {
      // Test before April 1st, we can fiddle with this.
      this.inputForm.controls.downPayment.setValue(this.hemnetService.minimumDownPayment())
    })
  }

  public ngOnInit(): void {
    // Reactive forms does not play nice with effects?
    this.hemnetService.hemnetData$.pipe(
      filter(d => !!d.askingPrice),
      take(1)
    ).subscribe(data => {
      this.inputForm.controls.askingPrice.setValue(data.askingPrice)
      this.inputForm.controls.downPayment.setValue(data.downPayment)
      this.inputForm.controls.fee.setValue(data.fee)
      this.inputForm.controls.runCost.setValue(data.runCost)
    })

    // Do all form changes
    this.inputForm.valueChanges
      .subscribe(v => {
        this.hemnetService.interestRate.set(v.interestRate)
        this.hemnetService.askingPrice.set(v.askingPrice)
        this.hemnetService.downPayment.set(v.downPayment)
        this.hemnetService.fee.set(v.fee)
        this.hemnetService.runCost.set(v.runCost)
        this.hemnetService.valid.set(this.inputForm.valid)
      })

    // Save data to LP after some delay
    this.inputForm.valueChanges
      .pipe(
        debounceTime(this.DELAY),
        switchMap(() => this.hemnetService.saveHemnetData())
      )
      .subscribe()
  }
}